import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Drawer, Popconfirm } from "antd";
import { ClipboardList, Gauge, LogOut, Menu, Settings } from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { BrandLogo } from "@/components/shared/BrandLogo";
import { routePaths } from "@/routes/routePaths";
import { logoutAdmin } from "@/services/adminApi";

const adminNavItems = [
  { to: routePaths.admin.root, label: "Dashboard", icon: Gauge, end: true },
  {
    to: routePaths.admin.waitlist,
    label: "Waitlist",
    icon: ClipboardList,
    end: false,
  },
  {
    to: routePaths.admin.settings,
    label: "Settings",
    icon: Settings,
    end: false,
  },
];

export function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logoutMutation = useMutation({
    mutationFn: logoutAdmin,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
      navigate(routePaths.admin.login, { replace: true });
    },
  });

  const renderNav = () => (
    <nav className="grid gap-2">
      {adminNavItems.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              [
                "inline-flex h-10 items-center gap-3 border px-3 text-sm transition",
                isActive
                  ? "border-neutral-950 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950"
                  : "border-neutral-300 text-neutral-700 hover:border-neutral-950 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-white",
              ].join(" ")
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  const renderLogoutButton = () => (
    <Popconfirm
      title="Log out?"
      description="Your admin session will end on this device."
      okText="Log out"
      cancelText="Cancel"
      onConfirm={() => logoutMutation.mutate()}
      okButtonProps={{ loading: logoutMutation.isPending }}
    >
      <button
        type="button"
        className="inline-flex h-10 w-full items-center gap-3 border border-neutral-300 px-3 text-left text-sm text-neutral-700 transition hover:border-neutral-950 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-white"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        <span>{logoutMutation.isPending ? "Logging out..." : "Log out"}</span>
      </button>
    </Popconfirm>
  );

  return (
    <div className="min-h-screen bg-white text-neutral-950 dark:bg-ink-900 dark:text-neutral-50 lg:flex">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-ink-900 lg:hidden">
        <div className="flex min-h-14 items-center justify-between px-4">
          <NavLink to={routePaths.admin.root} aria-label="Admin dashboard">
            <BrandLogo />
          </NavLink>
          <button
            type="button"
            aria-label="Open admin menu"
            onClick={() => setMenuOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center border border-neutral-300 text-neutral-700 transition hover:border-neutral-950 dark:border-neutral-700 dark:text-neutral-200 dark:hover:border-white"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <Drawer
        title={<BrandLogo />}
        placement="left"
        width={288}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        styles={{ body: { padding: 0 } }}
      >
        <div className="flex h-full flex-col bg-white text-neutral-950 dark:bg-ink-900 dark:text-neutral-50">
          <div className="flex-1 p-4">{renderNav()}</div>
          <div className="border-t border-neutral-200 p-4 dark:border-neutral-800">
            {renderLogoutButton()}
          </div>
        </div>
      </Drawer>

      <aside className="hidden min-h-screen w-64 shrink-0 flex-col border-r border-neutral-200 dark:border-neutral-800 lg:flex">
        <div className="border-b border-neutral-200 p-5 dark:border-neutral-800">
          <NavLink to={routePaths.admin.root} aria-label="Admin dashboard">
            <BrandLogo />
          </NavLink>
        </div>
        <div className="flex-1 p-4">{renderNav()}</div>
        <div className="border-t border-neutral-200 p-4 dark:border-neutral-800">
          {renderLogoutButton()}
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
